/**
 * A browser that froze inside Firestore's on-device storage uses its in-memory
 * cache for the next 24 hours (Miles, 2026-09-30: "only after a freeze").
 *
 * What the health record showed, the day every other freeze fix was live: the
 * long freezes left (up to 36 s for Malaika, up to 7.8 s for a teammate) all
 * came from Firestore's own IndexedDB callback (`simple_db.ts` wrapRequest,
 * whose PersistencePromise chain runs synchronously inside `onsuccess`), in the
 * first minutes after a page load. The one session on the in-memory cache that
 * day had no freeze past 0.2 s. So a browser that shows one such freeze starts
 * its next page loads without the on-device cache, and everyone else keeps it.
 *
 * The rule (measured in Chromium, 2026-09-30): a long animation frame whose
 * script was an `IDBRequest…` / `IDBTransaction…` event listener from this
 * page's own origin, running FREEZE_THRESHOLD_MS or more. Chromium names such a
 * script's invoker `IDBRequest.onsuccess` and counts the microtasks it starts
 * in the same entry. A browser extension's storage work is another origin, so
 * it never arms this.
 *
 * The switch takes effect at the next page load: a running Firestore cannot
 * change its cache, and nothing here reloads the page. The mark lives in
 * localStorage, so every new window of this browser honours it; it expires on
 * its own, and a mark further out than one period is read as damaged and
 * removed.
 *
 * A LEAF: it imports nothing, so the Firestore boot (on the first download)
 * can read it before initializeFirestore. Lever: REACT_APP_FS_FREEZE_FALLBACK=off
 * (read in utils/firestore.ts) neither honours nor arms the mark.
 */

/** localStorage key: this browser starts Firestore on its in-memory cache until the time it holds. */
export const FREEZE_FALLBACK_KEY = 'prest0_fs_freeze_fallback';
/** A storage callback that runs this long is a freeze. */
export const FREEZE_THRESHOLD_MS = 3_000;
/** How long a browser stays on the in-memory cache after a freeze. */
export const FREEZE_FALLBACK_TTL_MS = 24 * 60 * 60 * 1000;

const MARK_RE = /^memory:(\d{1,16})$/;

/** The parts of a long-animation-frame script entry the rule reads. */
export interface FrameScriptLike {
  invoker?: string;
  invokerType?: string;
  duration?: number;
  sourceURL?: string;
}

/** The parts of a long-animation-frame entry the rule reads. */
export interface FrameEntryLike {
  duration?: number;
  scripts?: readonly FrameScriptLike[];
}

function localStorageOrNull(): Storage | null {
  try {
    const ls = (globalThis as { localStorage?: Storage }).localStorage;
    return ls ?? null;
  } catch {
    return null;
  }
}

/** Is this script a storage callback from this page's own code, running a freeze's length? */
export function isStorageFreezeScript(script: FrameScriptLike, pageOrigin: string): boolean {
  if (!script || script.invokerType !== 'event-listener') return false;
  const invoker = typeof script.invoker === 'string' ? script.invoker : '';
  if (!/^IDB[A-Za-z]*\./.test(invoker)) return false;
  if (!(Number(script.duration) >= FREEZE_THRESHOLD_MS)) return false;
  if (typeof script.sourceURL !== 'string' || script.sourceURL === '') return false;
  try {
    return new URL(script.sourceURL).origin === pageOrigin;
  } catch {
    return false;
  }
}

/** The longest storage freeze in this frame, in ms, or 0 when it has none. */
export function storageFreezeMs(entry: FrameEntryLike, pageOrigin: string): number {
  let longest = 0;
  for (const script of entry?.scripts || []) {
    if (isStorageFreezeScript(script, pageOrigin)) longest = Math.max(longest, Math.round(Number(script.duration)));
  }
  return longest;
}

/** Is this browser marked to start Firestore on its in-memory cache? Removes an expired or damaged mark. */
export function readFreezeFallback(now: number = Date.now()): boolean {
  const ls = localStorageOrNull();
  if (!ls) return false;
  try {
    const raw = ls.getItem(FREEZE_FALLBACK_KEY);
    if (raw === null) return false;
    const match = MARK_RE.exec(raw);
    const until = match ? Number(match[1]) : 0;
    if (until > now && until <= now + FREEZE_FALLBACK_TTL_MS) return true;
    ls.removeItem(FREEZE_FALLBACK_KEY);
  } catch {
    /* unreadable: the on-device cache, as before */
  }
  return false;
}

/** Mark this browser for the next FREEZE_FALLBACK_TTL_MS. False when localStorage refused the write. */
export function markFreezeFallback(now: number = Date.now()): boolean {
  const ls = localStorageOrNull();
  if (!ls) return false;
  try {
    ls.setItem(FREEZE_FALLBACK_KEY, `memory:${now + FREEZE_FALLBACK_TTL_MS}`);
    return true;
  } catch {
    return false;
  }
}

export interface StorageFreeze {
  /** How long the storage callback ran. */
  ms: number;
  /** Whether the mark reached localStorage (false: the caller may keep a tab-only mark). */
  stored: boolean;
}

export interface FreezeWatchOptions {
  /** Clock, for tests. */
  now?: () => number;
  /** This page's origin, for tests. */
  origin?: string;
  /** The observer constructor, for tests. */
  Observer?: typeof PerformanceObserver;
}

/**
 * Watch this page's frames; at the first storage freeze, mark the browser and
 * call onFreeze once. Returns a stop function. Does nothing where the browser
 * cannot report long animation frames.
 */
export function watchForStorageFreezes(
  onFreeze: (freeze: StorageFreeze) => void,
  opts: FreezeWatchOptions = {},
): () => void {
  const Observer = opts.Observer ?? (globalThis as { PerformanceObserver?: typeof PerformanceObserver }).PerformanceObserver;
  const supported = (Observer as { supportedEntryTypes?: readonly string[] } | undefined)?.supportedEntryTypes;
  if (!Observer || !Array.isArray(supported) || !supported.includes('long-animation-frame')) return () => {};
  let origin = opts.origin;
  if (origin === undefined) {
    try {
      origin = (globalThis as { location?: Location }).location?.origin;
    } catch {
      origin = undefined;
    }
  }
  if (!origin) return () => {};
  const pageOrigin = origin;
  const now = opts.now ?? (() => Date.now());
  let done = false;
  let observer: PerformanceObserver | null = null;
  const stop = () => {
    done = true;
    try { observer?.disconnect(); } catch { /* already gone */ }
  };
  try {
    observer = new Observer((list) => {
      if (done) return;
      for (const entry of list.getEntries() as unknown as FrameEntryLike[]) {
        const ms = storageFreezeMs(entry, pageOrigin);
        if (ms === 0) continue;
        stop();
        const stored = markFreezeFallback(now());
        try { onFreeze({ ms, stored }); } catch { /* a listener never breaks the page */ }
        return;
      }
    });
    observer.observe({ type: 'long-animation-frame', buffered: true } as PerformanceObserverInit);
  } catch {
    stop();
  }
  return stop;
}
